import React, { useState } from "react";

// Redux
import { useSelector } from "react-redux";

// Styling
import { makeStyles } from "@material-ui/core/styles";

// Components
import { injectStripe } from "react-stripe-elements";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement,
} from "react-stripe-elements";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import FullContainerProgress from "../../../UI/FullContainerProgress";
import Box from "@material-ui/core/Box";
import MuiAlert from "@material-ui/lab/Alert";
import AddressComboBox from "../../../FormFields/CustomerAddressComboBox";
import StripeElementWrapper from "../../../FormFields/Stripe/StripeElementWrapper";
import TextField from "@material-ui/core/TextField";
import Divider from "@material-ui/core/Divider";
import ButtonGroup from "@material-ui/core/ButtonGroup";

//Styling
const useStyles = makeStyles((theme) => ({
  form: {
    "& .MuiFormControl-root": {
      minWidth: 250,
    },
  },
  maxWidth: {
    minWidth: 120,
    width: "100%",
  },
  alert: {
    marginTop: theme.spacing(1),
    width: "100%",
  },
  dialogContent: { overflow: "visible", paddingBottom: theme.spacing(5) },
}));

const CreatePaymentMethodDialog = ({ stripe, open, onClose, onSubmit }) => {
  //Styles
  const classes = useStyles();
  // Redux
  const { isLoading: isLoadingPaymentMethods, error: paymentMethodError } =
    useSelector((state) => state.paymentMethods);
  const { currentPageCustomer } = useSelector((state) => state.customers);
  const { customer_id, first_name, last_name } = currentPageCustomer;

  // Local State
  const [addressMode, setAddressMode] = useState("select"); // "select" | "create"
  const [address, setAddress] = useState(null);
  const [newLine1, setNewLine1] = useState("");
  const [newLine2, setNewLine2] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newState, setNewState] = useState("");
  const [newZip, setNewZip] = useState("");
  const [newCountry, setNewCountry] = useState("");
  const [stripeValidationError, setStripeValidationError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "There was an error. Please try again."
  );

  const getAddressFields = () => {
    if (addressMode === "create") {
      return {
        line_1: newLine1,
        line_2: newLine2,
        city: newCity,
        state: newState,
        zip: newZip,
        country: newCountry,
      };
    }
    return address;
  };

  const isSubmitDisabled = () => {
    if (addressMode === "select") return address === null;
    return !newLine1 || !newCity || !newState || !newZip || !newCountry;
  };

  const formSubmit = async () => {
    const { line_1, line_2, city, state, zip, country } = getAddressFields();
    const clientData = {
      name: first_name + " " + last_name,
      address_line1: line_1,
      address_line2: line_2,
      address_city: city,
      address_state: state,
      address_zip: zip,
      address_country: country,
    };

    const stripeRes = await stripe.createPaymentMethod("card", {
      billing_details: {
        name: clientData.name,
        address: {
          line1: clientData.address_line1,
          line2: clientData.address_line2,
          city: clientData.address_city,
          state: clientData.address_state,
          postal_code: clientData.address_zip,
          country: clientData.address_country,
        },
      },
    });
    let payment_method_id = "";
    let stripeValidationSuccess = false;
    if (stripeRes.error) {
      stripeValidationSuccess = false;
    } else if (stripeRes.paymentMethod) {
      payment_method_id = stripeRes.paymentMethod.id;
      stripeValidationSuccess = true;
    }

    if (stripeValidationSuccess) {
      const payload = { payment_method_id };
      if (addressMode === "create") {
        payload.newAddress = {
          line_1: newLine1,
          line_2: newLine2 || "",
          city: newCity,
          state: newState,
          zip: newZip + "",
          country: newCountry,
          others_details: "",
        };
      }
      onSubmit(payload);
    } else {
      const { message } = stripeRes.error;
      setStripeValidationError(true);
      setErrorMessage(message);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="alert-payment-method-view"
      aria-describedby="alert-payment-method-view"
      scroll="body"
    >
      {isLoadingPaymentMethods ? (
        <>
          <DialogContent>
            <Box width={700} height={500}>
              <FullContainerProgress />
            </Box>
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle id="alert-payment-method-view">
            Create Payment Method
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <form className={classes.form}>
              <Grid container spacing={4}>
                {(paymentMethodError || stripeValidationError) && (
                  <Grid item xs={12}>
                    <MuiAlert className={classes.alert} severity="error">
                      {errorMessage}
                    </MuiAlert>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="body1" component="p">
                    Customer ID: {customer_id}
                  </Typography>
                </Grid>

                {/* Customer Card info */}
                <Grid item xs={12}>
                  <StripeElementWrapper
                    label="Card Number"
                    component={CardNumberElement}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StripeElementWrapper
                    label="Expires"
                    component={CardExpiryElement}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StripeElementWrapper
                    label="CVC"
                    component={CardCVCElement}
                  />
                </Grid>

                {/* Customer Address linked to payment method */}
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Billing Address
                    </Typography>
                    <ButtonGroup size="small" variant="outlined">
                      <Button
                        color={addressMode === "select" ? "primary" : "default"}
                        variant={
                          addressMode === "select" ? "contained" : "outlined"
                        }
                        onClick={() => setAddressMode("select")}
                      >
                        Select Existing
                      </Button>
                      <Button
                        color={addressMode === "create" ? "primary" : "default"}
                        variant={
                          addressMode === "create" ? "contained" : "outlined"
                        }
                        onClick={() => setAddressMode("create")}
                      >
                        Create New
                      </Button>
                    </ButtonGroup>
                  </Box>
                  <Divider style={{ marginBottom: 16 }} />
                  {addressMode === "select" ? (
                    <AddressComboBox
                      customerId={customer_id}
                      onChange={(value) => setAddress(value)}
                    />
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Address Line 1"
                          fullWidth
                          required
                          value={newLine1}
                          onChange={(e) => setNewLine1(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          label="Address Line 2"
                          fullWidth
                          value={newLine2}
                          onChange={(e) => setNewLine2(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="City"
                          fullWidth
                          required
                          value={newCity}
                          onChange={(e) => setNewCity(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="State"
                          fullWidth
                          required
                          value={newState}
                          onChange={(e) => setNewState(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="Zip"
                          fullWidth
                          required
                          value={newZip}
                          onChange={(e) => setNewZip(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="Country"
                          fullWidth
                          required
                          value={newCountry}
                          onChange={(e) => setNewCountry(e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} variant="outlined">
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={isSubmitDisabled()}
              onClick={() => {
                formSubmit();
              }}
            >
              Create Payment Method
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default injectStripe(CreatePaymentMethodDialog);
